import { PropsWithChildren, RefObject, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import type { GenAiPanelHandle } from '@verbit-ai/ai-service-web'

import { InconsistencyItem, PreparedInconsistencies, getInconsistencyQuote, prepareInconsistencies } from 'src/models/InlineInconsistency'
import { analyticsService } from 'src/services/AnalyticsService'

import { useTranscriptHighLight } from './TranscriptHighLightContext'
import { useTranscriptViewer } from './TranscriptViewerProvider'

type ReportInconsistencies = (items: readonly InconsistencyItem[]) => void

type ChipPosition = { position: number; total: number }

type Selection = {
    selectedUuid: string | null
    select: (uuid: string, chip: ChipPosition) => void
}

const NO_ITEMS: readonly InconsistencyItem[] = Object.freeze([])

// Split so the AI panel, which only reports, does not re-render when the list or the selection changes.
const PreparedContext = createContext<PreparedInconsistencies>(prepareInconsistencies(undefined))
const ReporterContext = createContext<ReportInconsistencies>(() => undefined)
const SelectionContext = createContext<Selection>({ selectedUuid: null, select: () => undefined })
const PanelRefContext = createContext<RefObject<GenAiPanelHandle | null>>({ current: null })
const MarkSelectedContext = createContext<(uuid: string, highlightId: number) => void>(() => undefined)

type InlineInconsistenciesProviderProps = PropsWithChildren<{
    openAiPanel: () => void
}>

export const InlineInconsistenciesProvider = ({ children, openAiPanel }: InlineInconsistenciesProviderProps) => {
    const [items, setItems] = useState(NO_ITEMS)
    const prepared = useMemo(() => prepareInconsistencies(items), [items])
    const panelRef = useRef<GenAiPanelHandle | null>(null)

    const itemsRef = useRef(items)
    itemsRef.current = items

    const { highlight, selectTextToHighlight, selectTextToHighlightByOffset } = useTranscriptHighLight()
    const { stopAutoScrolling } = useTranscriptViewer()
    // The chip is selected only while the highlight it painted is the one on screen.
    const [selected, setSelected] = useState<{ uuid: string; highlightId: number } | null>(null)
    const selectedUuid = selected && highlight?.id === selected.highlightId ? selected.uuid : null
    const selectedUuidRef = useRef(selectedUuid)
    selectedUuidRef.current = selectedUuid

    const select = useCallback(
        (uuid: string, chip: ChipPosition) => {
            // Clicking the selected marker again clears it, with its highlight; the panel stays as it is.
            if (uuid === selectedUuidRef.current) {
                setSelected(null)
                selectTextToHighlight(null)

                return
            }

            const item = itemsRef.current.find((candidate) => candidate.uuid === uuid)
            if (!item) {
                return
            }

            const quote = getInconsistencyQuote(item)
            // The user is already looking at the quote: scroll only if it is (or, once the panel opens, becomes)
            // hidden, and keep live text and playing media from pulling it away.
            stopAutoScrolling()
            const highlightId = quote ? selectTextToHighlightByOffset(quote.offset, quote.turns, { reveal: 'ifHidden' }) : null
            setSelected(highlightId === null ? null : { uuid, highlightId })
            openAiPanel()
            panelRef.current?.openInconsistency(uuid)
            analyticsService.track('Insights - Inconsistencies inline chip clicked', chip)
        },
        [openAiPanel, selectTextToHighlight, selectTextToHighlightByOffset, stopAutoScrolling],
    )

    const selection = useMemo(() => ({ selectedUuid, select }), [selectedUuid, select])

    // A highlight from the panel's card selects its marker too, while that highlight stays on screen.
    const markSelected = useCallback((uuid: string, highlightId: number) => setSelected({ uuid, highlightId }), [])

    return (
        <ReporterContext.Provider value={setItems}>
            <PanelRefContext.Provider value={panelRef}>
                <MarkSelectedContext.Provider value={markSelected}>
                    <PreparedContext.Provider value={prepared}>
                        <SelectionContext.Provider value={selection}>{children}</SelectionContext.Provider>
                    </PreparedContext.Provider>
                </MarkSelectedContext.Provider>
            </PanelRefContext.Provider>
        </ReporterContext.Provider>
    )
}

export const usePreparedInconsistencies = () => useContext(PreparedContext)

export const useReportInconsistencies = () => useContext(ReporterContext)

export const useInconsistencySelection = () => useContext(SelectionContext)

export const useInconsistenciesPanelRef = () => useContext(PanelRefContext)

export const useMarkInconsistencySelected = () => useContext(MarkSelectedContext)
